import { protectorApiClient, type RequestProps } from '@protector/api-client';
import type { FeatureCollection, Geometry, Point } from 'geojson';
import type {
  AssetGroup,
  DigitalTrapNetwork,
  DigitalTrapNetworksResponse,
  FlightStagePredictions,
  NetworkNote,
  NetworkNoteRequest,
  NetworkNotesResponse,
  PredictionsRequest,
  RegionalOverviewGroupsResponse,
  RegionalOverviewDigitalTrapNetworkCountProperties,
  RegionalOverviewDiseaseRiskTechnician
} from './models.ts';
import { DIGITAL_TRAPS_SOURCE, DIGITAL_TRAP_NETWORKS_REFERENCE_DATE } from './constants.ts';
import { manageGroupNameResponseSchema } from './schemas.ts';

function requestWithDomain<I, O>(args: Omit<RequestProps<I, O>, 'domain'>) {
  return protectorApiClient.request<I, O>({ ...args, domain: 'RegionalOverview' });
}

export async function getRegionalOverviewGroups(organizationId: string): Promise<RegionalOverviewGroupsResponse> {
  const response = await requestWithDomain<unknown, RegionalOverviewGroupsResponse>({
    config: {
      method: 'GET',
      url: `/v2/regional-overview/groups/organization_id/${organizationId}`,
      headers: { 'x-company-id': organizationId }
    }
  });
  return response.data;
}

export async function manageAssetGroup(group: AssetGroup) {
  const urlParams = group.custom_group ? `/${group.id}` : '';
  return requestWithDomain({
    config: {
      method: group.custom_group ? 'PUT' : 'POST',
      url: `/v2/regional-overview/groups${urlParams}`,
      data: group,
      headers: { 'X-Company-Id': group.organization_id }
    },
    outputSchema: manageGroupNameResponseSchema
  });
}

export async function getDigitalTrapsPredictions(organizationId: string, assetIds: string[]): Promise<FlightStagePredictions[]> {
  const requestBody: PredictionsRequest = { asset_ids: assetIds };
  const response = await requestWithDomain<unknown, FlightStagePredictions[]>({
    config: {
      method: 'POST',
      url: '/v2/regional-overview/predictions',
      data: requestBody,
      headers: { 'x-company-id': organizationId }
    }
  });
  return response.data;
}

export async function getAllRegionalOverviewResultsV2<T extends Geometry, PropertiesType>(
  source: string,
  referenceDate: string,
  useMock?: boolean
): Promise<{ features: FeatureCollection<T, PropertiesType>; hasShiftData: boolean }> {
  const headers: Record<string, string | boolean> = {};
  if (useMock) headers['x-demo'] = true;
  const response = await requestWithDomain<unknown, FeatureCollection<T, PropertiesType>>({
    config: {
      method: 'GET',
      url: `/v2/regional-overview/${source}/${referenceDate}`,
      headers
    }
  });
  return {
    features: response.data,
    hasShiftData: response.headers?.['x-has-shift-data'] === 'true'
  };
}

export async function getRegionalOverviewTechnicians(useMock?: boolean): Promise<RegionalOverviewDiseaseRiskTechnician[]> {
  const headers: Record<string, string | boolean> = {};
  if (useMock) headers['x-demo'] = true;
  const response = await requestWithDomain<unknown, RegionalOverviewDiseaseRiskTechnician[]>({
    config: {
      method: 'GET',
      url: '/v2/regional-overview/disease-risk/technicians',
      headers
    }
  });
  return response.data;
}

export async function getDigitalTrapNetworks(organizationId: string): Promise<DigitalTrapNetwork[]> {
  const response = await requestWithDomain<unknown, DigitalTrapNetworksResponse>({
    config: {
      method: 'GET',
      url: '/v2/regional-overview/digital-traps/groups',
      headers: { 'X-Company-Id': organizationId }
    }
  });
  return response.data.content;
}

export async function getDigitalTrapNetworkTrapCounts(): Promise<Record<string, number>> {
  const { features } = await getAllRegionalOverviewResultsV2<Point, RegionalOverviewDigitalTrapNetworkCountProperties>(
    DIGITAL_TRAPS_SOURCE,
    DIGITAL_TRAP_NETWORKS_REFERENCE_DATE
  );
  const trapCountsByNetworkId: Record<string, number> = {};

  features.features.forEach(feature => {
    const networkId = feature.properties.group.id;
    trapCountsByNetworkId[networkId] = (trapCountsByNetworkId[networkId] ?? 0) + 1;
  });

  return trapCountsByNetworkId;
}

export async function getNetworkNotes(organizationId: string): Promise<NetworkNote[]> {
  const response = await requestWithDomain<unknown, NetworkNotesResponse>({
    config: {
      method: 'GET',
      url: '/v2/regional-overview/digital-traps/network-notes',
      headers: { 'X-Company-Id': organizationId }
    }
  });
  return response.data.content;
}

export async function saveNetworkNote(organizationId: string, networkId: string, data: NetworkNoteRequest): Promise<NetworkNote> {
  const response = await requestWithDomain<unknown, NetworkNote>({
    config: {
      method: 'PUT',
      url: `/v2/regional-overview/digital-traps/network-notes/${encodeURIComponent(networkId)}`,
      data,
      headers: { 'X-Company-Id': organizationId }
    }
  });
  return response.data;
}

export async function deleteNetworkNote(organizationId: string, networkId: string): Promise<void> {
  await requestWithDomain<unknown, void>({
    config: {
      method: 'DELETE',
      url: `/v2/regional-overview/digital-traps/network-notes/${encodeURIComponent(networkId)}`,
      headers: { 'X-Company-Id': organizationId }
    }
  });
}

export async function createManualTrap(data: import('./models.ts').ManualTrapCreateRequest): Promise<void> {
  await requestWithDomain<import('./models.ts').ManualTrapCreateRequest, unknown>({
    config: {
      method: 'POST',
      url: '/v2/regional-overview/manual-traps',
      headers: { 'X-Company-Id': data.organization_id },
      data
    }
  });
}
